import type { SessionUser } from '@ai-sales/shared'
import { DropdownMenu } from 'radix-ui'
import { useLogout } from '../auth/session-context.js'
import { IconChevronDown, IconLogout, IconMonitor, IconMoon, IconSun } from '../components/icon.js'
import { cn } from '../lib/cn.js'
import { useTheme } from './theme.js'

/**
 * Меню пользователя.
 *
 * Выбор темы стоит здесь тремя кнопками в строку, а не тремя пунктами
 * списка. Разница не косметическая: пункт списка закрывает меню при
 * нажатии, поэтому сравнить светлую и тёмную можно было только открывая
 * меню заново. Кнопки не закрывают меню — тема меняется под курсором,
 * и видно, что выбираешь.
 */
export function UserMenu({ user }: { user: SessionUser }) {
  const logout = useLogout()
  const { theme, setTheme } = useTheme()

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors',
          'hover:bg-line-soft data-[state=open]:bg-line-soft',
        )}
      >
        <span
          aria-hidden="true"
          className="grid size-7 shrink-0 place-items-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent"
        >
          {initials(user)}
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
          {user.name ?? user.email}
        </span>
        <IconChevronDown size={13} className="shrink-0 text-muted" />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          side="top"
          sideOffset={6}
          className={cn(
            'z-50 min-w-64 rounded-xl border border-line bg-raised p-1.5',
            'shadow-[0_16px_40px_-16px_rgba(23,26,23,0.3)]',
          )}
        >
          <div className="flex items-center gap-2.5 px-2 py-2">
            <span
              aria-hidden="true"
              className="grid size-9 shrink-0 place-items-center rounded-full bg-accent-soft text-xs font-semibold text-accent"
            >
              {initials(user)}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-medium text-ink">
                {user.name ?? 'Без имени'}
              </span>
              <span className="block truncate text-xs text-muted">{user.email}</span>
            </span>
          </div>

          <DropdownMenu.Separator className="mx-1 my-1 h-px bg-line-soft" />

          <DropdownMenu.RadioGroup
            value={theme}
            onValueChange={(value) => setTheme(value as typeof theme)}
            className="flex items-center justify-between gap-2 px-2 py-1"
          >
            <span className="text-[13px] text-ink-soft">Оформление</span>

            <span className="flex items-center gap-0.5 rounded-lg border border-line p-0.5">
              {THEME_OPTIONS.map(({ value, label, Icon }) => (
                <DropdownMenu.RadioItem
                  key={value}
                  value={value}
                  title={label}
                  aria-label={label}
                  /** Меню не закрывается: тему выбирают, глядя на неё. */
                  onSelect={(event) => event.preventDefault()}
                  className={cn(
                    'grid size-7 cursor-pointer place-items-center rounded-md outline-none transition-colors',
                    theme === value
                      ? 'bg-accent-soft text-accent'
                      : 'text-muted data-highlighted:bg-line-soft data-highlighted:text-ink-soft',
                  )}
                >
                  <Icon size={15} />
                </DropdownMenu.RadioItem>
              ))}
            </span>
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className="mx-1 my-1 h-px bg-line-soft" />

          <DropdownMenu.Item
            onSelect={() => logout.mutate()}
            className={cn(
              'flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] outline-none',
              'text-ink-soft data-highlighted:bg-crimson-soft data-highlighted:text-crimson',
            )}
          >
            <IconLogout size={15} className="shrink-0" />
            {logout.isPending ? 'Выходим…' : 'Выйти'}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

const THEME_OPTIONS = [
  { value: 'system', label: 'Как в системе', Icon: IconMonitor },
  { value: 'light', label: 'Светлая тема', Icon: IconSun },
  { value: 'dark', label: 'Тёмная тема', Icon: IconMoon },
] as const

function initials(user: SessionUser): string {
  const source = user.name?.trim() || user.email

  return source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}
